import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Stack, FormControlLabel, Checkbox,
    Typography, Box, Chip, Paper, List, ListItem, Divider
} from "@mui/material";
import dayjs from "dayjs";
import Note from "../../../MasterEventObjects/Note";
import { capitalizeFirstLetter } from "../../Helpers/TextStyling";
import ListItemText from "@mui/material/ListItemText";



function NotesEditDialog({ open, note, onClose, onSave, viewType, timeTable, attendees, currentUserId }) {

    console.log("NotesEditDialog : ", note);

    // --- Local State ---
    const [text, setText] = useState('');
    const [isPrivate, setIsPrivate] = useState(false);
    const [priority, setPriority] = useState('low');
    const [tagged, setTagged] = useState([]);
    const [selectedDates, setSelectedDates] = useState([]);
    const [selectedDays, setSelectedDays] = useState([]);

    // --- Sync State when Note changes (Edit mode vs Add mode) ---
    useEffect(() => {
        if (note) {
            setText(note.note || '');
            setIsPrivate(note.private || false);
            setPriority(note.priority || 'low');
            setTagged(note.tagged || []);

            // Load the existing note's position into the selection state
            setSelectedDates(note.date ? [note.date] : []);
            setSelectedDays(note.day ? [note.day] : []);
        } else {
            setText('');
            setIsPrivate(false);
            setPriority('low');
            setTagged([]);
            setSelectedDates([]);
            setSelectedDays([]);
        }
    }, [note, open]);

    const handleSave = () => {
        if (!text.trim()) return;

        if (note) {
            // EDIT MODE: Return single updated note
            onSave({
                ...note,
                note: text,
                private: isPrivate,
                priority,
                tagged,
                date: viewType === 'dates' ? selectedDates[0] : "",
                day: viewType === 'days' ? selectedDays[0] : ""
            });
        } else {
            // ADD MODE: Create array of notes based on selected dates/days
            const newNotes = [];
            const targetCollection = viewType === 'dates' ? selectedDates : selectedDays;

            targetCollection.forEach(selection => {
                const newNote = new Note();
                newNote.attendee_id = currentUserId;
                newNote.note = text;
                newNote.private = isPrivate;
                newNote.priority = priority;
                newNote.tagged = tagged;

                if (viewType === 'dates') {
                    newNote.date = selection;
                    // Auto-calculate day string if possible
                    const start = timeTable?.dates?.[0]?.date;
                    newNote.day = start ? `day ${dayjs(selection).diff(dayjs(start), 'day') + 1}` : "";
                } else {
                    newNote.day = selection;
                    newNote.date = "";
                }
                newNotes.push(JSON.parse(JSON.stringify(newNote)));
            });

            onSave(newNotes);
        }
    };

    const toggleTag = (id) => {
        setTagged(prev => prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]);
    };

    const toggleSelection = (val, isDate) => {
        const setter = isDate ? setSelectedDates : setSelectedDays;
        setter(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);
    };

    return (


        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: '24px' } }}>
            <DialogTitle sx={{ fontWeight: 900, color: '#1e293b' }}>
                {note ? 'Edit Note' : 'Create New Note'}
            </DialogTitle>

            <DialogContent dividers sx={{ borderBottom: 'none' }}>
                {/* 1. TEXT INPUT */}
                <TextField
                    autoFocus
                    multiline
                    rows={4}
                    fullWidth
                    variant="outlined"
                    placeholder="What's the briefing?"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '16px', bgcolor: '#f8fafc' } }}
                />

                {/* 2. PRIORITY SELECTOR */}
                <Box sx={{ mt: 3 }}>
                    <Typography variant="caption" sx={{ fontWeight: 900, color: '#94a3b8', ml: 1 }}>PRIORITY LEVEL</Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        {['low', 'medium', 'high'].map((level) => {
                            const isActive = priority === level;
                            const colors = { low: '#10b981', medium: '#f59e0b', high: '#ef4444' };
                            return (
                                <Chip
                                    key={level}
                                    label={level.toUpperCase()}
                                    onClick={() => setPriority(level)}
                                    sx={{
                                        fontWeight: 900,
                                        fontSize: '10px',
                                        bgcolor: isActive ? `${colors[level]}22` : 'transparent',
                                        color: isActive ? colors[level] : '#94a3b8',
                                        border: `1px solid ${isActive ? colors[level] : '#e2e8f0'}`,
                                        '&:hover': { bgcolor: `${colors[level]}11` }
                                    }}
                                />
                            );
                        })}
                    </Stack>
                </Box>

                {/* 3. SETTINGS & TAGGING */}
                <Stack direction="row" justifyContent="space-between" sx={{ mt: 3, mb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 900, color: '#94a3b8', ml: 1 }}>TAG ATTENDEES</Typography>
                    <FormControlLabel
                        control={<Checkbox size="small" checked={isPrivate} onChange={(e) => setIsPrivate(e.target.checked)} />}
                        label={<Typography sx={{ fontSize: '11px', fontWeight: 700 }}>Private Note</Typography>}
                    />
                </Stack>

                <Paper variant="outlined" sx={{ borderRadius: '16px', maxHeight: 120, overflow: 'auto', bgcolor: '#f8fafc' }}>
                    <List dense>
                        {attendees.filter(a => a.id !== currentUserId).map(att => (
                            <ListItem key={att.id} button onClick={() => toggleTag(att.id)} sx={{ borderRadius: '8px' }}>
                                <Checkbox size="small" checked={tagged.includes(att.id)} />
                                <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>{att.nickname || att.fname}</Typography>
                            </ListItem>
                        ))}
                    </List>
                </Paper>

                {/* 4. DATE/DAY SELECTION (For New & Existing Notes) */}
                <Box sx={{ mt: 3 }}>
                    <Typography variant="caption" sx={{ fontWeight: 900, color: '#94a3b8', ml: 1 }}>
                        {note ? `ASSIGN TO ${viewType.slice(0, -1).toUpperCase()}` : `APPLY TO ${viewType.toUpperCase()}`}
                    </Typography>

                    <Paper variant="outlined" sx={{ mt: 1, borderRadius: '16px', maxHeight: 180, overflow: 'auto', bgcolor: '#f8fafc' }}>
                        <List dense>
                            {viewType === 'dates' ? (
                                // --- DATES RENDER ---
                                timeTable?.dates?.map((d) => {
                                    const isChecked = selectedDates.includes(d.date);
                                    return (
                                        <ListItem
                                            key={d.date}
                                            button
                                            onClick={() => {
                                                if (note) setSelectedDates([d.date]); // Edit mode: Single select
                                                else toggleSelection(d.date, true);   // Add mode: Multi select
                                            }}
                                            sx={{ borderRadius: '8px' }}
                                        >
                                            <Checkbox
                                                size="small"
                                                checked={isChecked}
                                                // Show radio-style if editing, checkbox if adding
                                                sx={note ? { '& .MuiSvgIcon-root': { borderRadius: '50%' } } : {}}
                                            />
                                            <ListItemText
                                                primary={dayjs(d.date).format('dddd, MMM D')}
                                                primaryTypographyProps={{ fontSize: '12px', fontWeight: isChecked ? 800 : 500 }}
                                            />
                                        </ListItem>
                                    );
                                })
                            ) : (
                                // --- DAYS RENDER (Fixed "End_time" bug) ---
                                timeTable?.days?.map((d, idx) => {
                                    const dayKey = Object.keys(d).find(k => k.toLowerCase().includes('day')) || `day ${idx + 1}`;
                                    const dayName = d[dayKey];
                                    const isChecked = selectedDays.includes(dayKey);

                                    return (
                                        <ListItem
                                            key={dayKey}
                                            button
                                            onClick={() => {
                                                if (note) setSelectedDays([dayKey]); // Edit mode: Single select
                                                else toggleSelection(dayKey, false); // Add mode: Multi select
                                            }}
                                            sx={{ borderRadius: '8px' }}
                                        >
                                            <Checkbox
                                                size="small"
                                                checked={isChecked}
                                                sx={note ? { '& .MuiSvgIcon-root': { borderRadius: '50%' } } : {}}
                                            />
                                            <ListItemText
                                                primary={`${capitalizeFirstLetter(dayKey)} (${dayName})`}
                                                primaryTypographyProps={{ fontSize: '12px', fontWeight: isChecked ? 800 : 500 }}
                                            />
                                        </ListItem>
                                    );
                                })
                            )}
                        </List>
                    </Paper>
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 3 }}>
                <Button onClick={onClose} sx={{ color: '#94a3b8', fontWeight: 700 }}>Cancel</Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={!text.trim() || (!note && selectedDates.length === 0 && selectedDays.length === 0)}
                    sx={{
                        borderRadius: '12px',
                        px: 4,
                        bgcolor: '#1e293b',
                        '&:hover': { bgcolor: '#334155' }
                    }}
                >
                    {note ? 'Update' : 'Save Note'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default NotesEditDialog;